/**
 * Le lexique: les mots qu'on substitue dans les modeles de phrases.
 *
 * TOUT le probleme d'un jeu de grammaire est la: si l'on remplace « chat » par
 * « ecole » dans « le ___ est petit », il faut obtenir « l'école est petite »
 * et non « le école est petit ». Chaque mot porte donc son genre, son pluriel
 * et son elision, et les determinants passent par des fonctions — jamais par
 * une concatenation ecrite a la main.
 *
 * Le code est en ASCII, les mots sont accentues: c'est l'enfant qui les lit.
 */

/** Un nom commun, avec ce qu'il faut pour l'accorder. */
export interface Nom {
  /** Singulier. */
  s: string
  /** Pluriel. */
  p: string
  genre: 'm' | 'f'
  /** Vrai si le mot commence par une voyelle ou un h muet (elision). */
  elide: boolean
}

export type CategorieNom = 'animaux' | 'objets' | 'aliments' | 'lieux'

export const NOMS: Record<CategorieNom, readonly Nom[]> = {
  animaux: [
    { s: 'chat', p: 'chats', genre: 'm', elide: false },
    { s: 'chien', p: 'chiens', genre: 'm', elide: false },
    { s: 'lapin', p: 'lapins', genre: 'm', elide: false },
    { s: 'oiseau', p: 'oiseaux', genre: 'm', elide: true },
    { s: 'tortue', p: 'tortues', genre: 'f', elide: false },
    { s: 'vache', p: 'vaches', genre: 'f', elide: false },
    { s: 'poule', p: 'poules', genre: 'f', elide: false },
    { s: 'écureuil', p: 'écureuils', genre: 'm', elide: true },
    { s: 'cheval', p: 'chevaux', genre: 'm', elide: false },
    { s: 'souris', p: 'souris', genre: 'f', elide: false },
  ],
  objets: [
    { s: 'crayon', p: 'crayons', genre: 'm', elide: false },
    { s: 'cahier', p: 'cahiers', genre: 'm', elide: false },
    { s: 'livre', p: 'livres', genre: 'm', elide: false },
    { s: 'gomme', p: 'gommes', genre: 'f', elide: false },
    { s: 'règle', p: 'règles', genre: 'f', elide: false },
    { s: 'chaise', p: 'chaises', genre: 'f', elide: false },
    { s: 'ballon', p: 'ballons', genre: 'm', elide: false },
    { s: 'bille', p: 'billes', genre: 'f', elide: false },
    { s: 'carte', p: 'cartes', genre: 'f', elide: false },
    { s: 'autocollant', p: 'autocollants', genre: 'm', elide: true },
  ],
  aliments: [
    { s: 'pomme', p: 'pommes', genre: 'f', elide: false },
    { s: 'poire', p: 'poires', genre: 'f', elide: false },
    { s: 'banane', p: 'bananes', genre: 'f', elide: false },
    { s: 'fraise', p: 'fraises', genre: 'f', elide: false },
    { s: 'biscuit', p: 'biscuits', genre: 'm', elide: false },
    { s: 'gâteau', p: 'gâteaux', genre: 'm', elide: false },
    { s: 'carotte', p: 'carottes', genre: 'f', elide: false },
    { s: 'orange', p: 'oranges', genre: 'f', elide: true },
  ],
  lieux: [
    { s: 'école', p: 'écoles', genre: 'f', elide: true },
    { s: 'maison', p: 'maisons', genre: 'f', elide: false },
    { s: 'jardin', p: 'jardins', genre: 'm', elide: false },
    { s: 'parc', p: 'parcs', genre: 'm', elide: false },
    { s: 'forêt', p: 'forêts', genre: 'f', elide: false },
    { s: 'cabane', p: 'cabanes', genre: 'f', elide: false },
    { s: 'plage', p: 'plages', genre: 'f', elide: false },
  ],
}

/** Les noms qui se comptent: ceux qu'on peut mettre dans un probleme ecrit. */
export const NOMS_COMPTABLES: readonly Nom[] = [...NOMS.aliments, ...NOMS.objets]

/** Un prenom, avec son genre: « il » ou « elle » en depend. */
export interface Prenom {
  nom: string
  genre: 'm' | 'f'
}

export const PRENOMS: readonly Prenom[] = [
  { nom: 'Amélia', genre: 'f' },
  { nom: 'Zac', genre: 'm' },
  { nom: 'Elliot', genre: 'm' },
  { nom: 'Léa', genre: 'f' },
  { nom: 'Noah', genre: 'm' },
  { nom: 'Mia', genre: 'f' },
  { nom: 'Thomas', genre: 'm' },
  { nom: 'Rosalie', genre: 'f' },
  { nom: 'Félix', genre: 'm' },
  { nom: 'Alice', genre: 'f' },
]

// -------------------------------------------------------------- determinants

/**
 * Colle un determinant a son nom. « l' » se colle, « le » prend une espace:
 * ecrire `${det} ${mot}` partout donnait « l' école ».
 */
export function coller(determinant: string, mot: string): string {
  return determinant.endsWith('’') ? `${determinant}${mot}` : `${determinant} ${mot}`
}

/** le / la / l’ / les, colle au nom. */
export function defini(n: Nom, pluriel = false): string {
  if (pluriel) return coller('les', n.p)
  if (n.elide) return coller('l’', n.s)
  return coller(n.genre === 'm' ? 'le' : 'la', n.s)
}

/** un / une / des, colle au nom. */
export function indefini(n: Nom, pluriel = false): string {
  if (pluriel) return coller('des', n.p)
  return coller(n.genre === 'm' ? 'un' : 'une', n.s)
}

/** ce / cet / cette / ces. « cet » devant une voyelle au masculin. */
export function demonstratif(n: Nom, pluriel = false): string {
  if (pluriel) return coller('ces', n.p)
  if (n.genre === 'f') return coller('cette', n.s)
  return coller(n.elide ? 'cet' : 'ce', n.s)
}

/** mon / ma / mes. « mon école » et non « ma école ». */
export function possessif(n: Nom, pluriel = false): string {
  if (pluriel) return coller('mes', n.p)
  if (n.genre === 'm' || n.elide) return coller('mon', n.s)
  return coller('ma', n.s)
}

/** Un nombre suivi de son nom, accorde: « 1 pomme », « 5 pommes ». */
export function compte(k: number, n: Nom): string {
  return `${k} ${k > 1 ? n.p : n.s}`
}

/**
 * « de poires », mais « d’oranges ». La preposition s'elide elle aussi, et
 * « Combien y a-t-il de oranges ? » saute aux yeux d'un enfant de 8 ans.
 */
export function deNom(n: Nom, pluriel = true): string {
  const mot = pluriel ? n.p : n.s
  return n.elide ? `d’${mot}` : `de ${mot}`
}

// ---------------------------------------------------------------- adjectifs

/**
 * Un adjectif et ses quatre formes.
 *
 * `pour` dit a QUOI il peut se rapporter. Sans ce champ, la substitution
 * fabriquait « La carte est heureuse » et « Les souris sont neuves »: la
 * grammaire etait juste, la phrase absurde — et un enfant qui ne comprend pas
 * la phrase ne peut pas repondre a la question.
 */
export interface Adjectif {
  ms: string
  fs: string
  mp: string
  fp: string
  /** Niveau a partir duquel on l'utilise (les irreguliers arrivent plus tard). */
  niveau: number
  pour: 'tout' | 'anime' | 'objet'
}

export const ADJECTIFS: readonly Adjectif[] = [
  { ms: 'petit', fs: 'petite', mp: 'petits', fp: 'petites', niveau: 1, pour: 'tout' },
  { ms: 'grand', fs: 'grande', mp: 'grands', fp: 'grandes', niveau: 1, pour: 'tout' },
  { ms: 'joli', fs: 'jolie', mp: 'jolis', fp: 'jolies', niveau: 1, pour: 'tout' },
  { ms: 'vert', fs: 'verte', mp: 'verts', fp: 'vertes', niveau: 1, pour: 'objet' },
  { ms: 'noir', fs: 'noire', mp: 'noirs', fp: 'noires', niveau: 1, pour: 'tout' },
  { ms: 'bleu', fs: 'bleue', mp: 'bleus', fp: 'bleues', niveau: 2, pour: 'objet' },
  { ms: 'fatigué', fs: 'fatiguée', mp: 'fatigués', fp: 'fatiguées', niveau: 2, pour: 'anime' },
  { ms: 'gentil', fs: 'gentille', mp: 'gentils', fp: 'gentilles', niveau: 3, pour: 'anime' },
  { ms: 'heureux', fs: 'heureuse', mp: 'heureux', fp: 'heureuses', niveau: 3, pour: 'anime' },
  { ms: 'doux', fs: 'douce', mp: 'doux', fp: 'douces', niveau: 3, pour: 'anime' },
  { ms: 'blanc', fs: 'blanche', mp: 'blancs', fp: 'blanches', niveau: 3, pour: 'tout' },
  { ms: 'neuf', fs: 'neuve', mp: 'neufs', fp: 'neuves', niveau: 3, pour: 'objet' },
]

/** Les adjectifs qu'on peut dire d'un animal, ou d'un objet. */
export function adjectifsPour(cible: 'anime' | 'objet', niveauMax: number): readonly Adjectif[] {
  return ADJECTIFS.filter((a) => a.niveau <= niveauMax && (a.pour === 'tout' || a.pour === cible))
}

/** La bonne forme de l'adjectif pour un genre et un nombre donnes. */
export function accorder(a: Adjectif, genre: 'm' | 'f', pluriel: boolean): string {
  if (genre === 'm') return pluriel ? a.mp : a.ms
  return pluriel ? a.fp : a.fs
}

/** Les quatre formes, dans l'ordre ms, fs, mp, fp: les choix d'un exercice. */
export function formesAdjectif(a: Adjectif): readonly string[] {
  return [a.ms, a.fs, a.mp, a.fp]
}

// -------------------------------------------------------------- conjugaison

export type Temps = 'present' | 'imparfait' | 'futur' | 'passe-compose'

export const NOM_TEMPS: Record<Temps, string> = {
  present: 'présent',
  imparfait: 'imparfait',
  futur: 'futur simple',
  'passe-compose': 'passé composé',
}

/**
 * Le meme nom, mais introduit: « au présent », « à l’imparfait ». Ecrire
 * `au ${NOM_TEMPS[t]}` donnait « au imparfait » une fois sur quatre.
 */
export const AU_TEMPS: Record<Temps, string> = {
  present: 'au présent',
  imparfait: 'à l’imparfait',
  futur: 'au futur simple',
  'passe-compose': 'au passé composé',
}

/** Les six personnes, dans l'ordre habituel des tableaux de conjugaison. */
export const PERSONNES: readonly string[] = [
  '1re personne du singulier',
  '2e personne du singulier',
  '3e personne du singulier',
  '1re personne du pluriel',
  '2e personne du pluriel',
  '3e personne du pluriel',
]

const PRONOMS: readonly string[] = ['je', 'tu', 'il', 'nous', 'vous', 'ils']

/**
 * Un verbe et ses tableaux, ecrits a la main.
 *
 * On ne conjugue PAS par regles: une regle qui se trompe fabrique une faute que
 * l'enfant apprend. Les formes sont donc des donnees, verifiees une fois.
 * `aux: 'etre'` sert a exclure le passe compose des exercices — « je suis
 * allé(e) » demanderait l'accord avec le sujet, ce qui est un autre exercice
 * (voir les accords du participe passe).
 */
export interface Verbe {
  infinitif: string
  /** Niveau a partir duquel le verbe apparait. */
  niveau: number
  /** Rang de difficulte, 1 (facile) a 6. */
  rang: number
  aux: 'avoir' | 'etre'
  present: readonly string[]
  imparfait: readonly string[]
  futur: readonly string[]
  passeCompose: readonly string[]
}

export const VERBES: readonly Verbe[] = [
  {
    infinitif: 'être',
    niveau: 1,
    rang: 2,
    aux: 'avoir',
    present: ['suis', 'es', 'est', 'sommes', 'êtes', 'sont'],
    imparfait: ['étais', 'étais', 'était', 'étions', 'étiez', 'étaient'],
    futur: ['serai', 'seras', 'sera', 'serons', 'serez', 'seront'],
    passeCompose: ['ai été', 'as été', 'a été', 'avons été', 'avez été', 'ont été'],
  },
  {
    infinitif: 'avoir',
    niveau: 1,
    rang: 2,
    aux: 'avoir',
    present: ['ai', 'as', 'a', 'avons', 'avez', 'ont'],
    imparfait: ['avais', 'avais', 'avait', 'avions', 'aviez', 'avaient'],
    futur: ['aurai', 'auras', 'aura', 'aurons', 'aurez', 'auront'],
    passeCompose: ['ai eu', 'as eu', 'a eu', 'avons eu', 'avez eu', 'ont eu'],
  },
  {
    infinitif: 'aimer',
    niveau: 1,
    rang: 1,
    aux: 'avoir',
    present: ['aime', 'aimes', 'aime', 'aimons', 'aimez', 'aiment'],
    imparfait: ['aimais', 'aimais', 'aimait', 'aimions', 'aimiez', 'aimaient'],
    futur: ['aimerai', 'aimeras', 'aimera', 'aimerons', 'aimerez', 'aimeront'],
    passeCompose: ['ai aimé', 'as aimé', 'a aimé', 'avons aimé', 'avez aimé', 'ont aimé'],
  },
  {
    infinitif: 'jouer',
    niveau: 1,
    rang: 1,
    aux: 'avoir',
    present: ['joue', 'joues', 'joue', 'jouons', 'jouez', 'jouent'],
    imparfait: ['jouais', 'jouais', 'jouait', 'jouions', 'jouiez', 'jouaient'],
    futur: ['jouerai', 'joueras', 'jouera', 'jouerons', 'jouerez', 'joueront'],
    passeCompose: ['ai joué', 'as joué', 'a joué', 'avons joué', 'avez joué', 'ont joué'],
  },
  {
    infinitif: 'manger',
    niveau: 2,
    rang: 2,
    aux: 'avoir',
    present: ['mange', 'manges', 'mange', 'mangeons', 'mangez', 'mangent'],
    imparfait: ['mangeais', 'mangeais', 'mangeait', 'mangions', 'mangiez', 'mangeaient'],
    futur: ['mangerai', 'mangeras', 'mangera', 'mangerons', 'mangerez', 'mangeront'],
    passeCompose: ['ai mangé', 'as mangé', 'a mangé', 'avons mangé', 'avez mangé', 'ont mangé'],
  },
  {
    infinitif: 'chanter',
    niveau: 2,
    rang: 2,
    aux: 'avoir',
    present: ['chante', 'chantes', 'chante', 'chantons', 'chantez', 'chantent'],
    imparfait: ['chantais', 'chantais', 'chantait', 'chantions', 'chantiez', 'chantaient'],
    futur: ['chanterai', 'chanteras', 'chantera', 'chanterons', 'chanterez', 'chanteront'],
    passeCompose: ['ai chanté', 'as chanté', 'a chanté', 'avons chanté', 'avez chanté', 'ont chanté'],
  },
  {
    infinitif: 'finir',
    niveau: 3,
    rang: 3,
    aux: 'avoir',
    present: ['finis', 'finis', 'finit', 'finissons', 'finissez', 'finissent'],
    imparfait: ['finissais', 'finissais', 'finissait', 'finissions', 'finissiez', 'finissaient'],
    futur: ['finirai', 'finiras', 'finira', 'finirons', 'finirez', 'finiront'],
    passeCompose: ['ai fini', 'as fini', 'a fini', 'avons fini', 'avez fini', 'ont fini'],
  },
  {
    infinitif: 'aller',
    niveau: 3,
    rang: 4,
    aux: 'etre',
    present: ['vais', 'vas', 'va', 'allons', 'allez', 'vont'],
    imparfait: ['allais', 'allais', 'allait', 'allions', 'alliez', 'allaient'],
    futur: ['irai', 'iras', 'ira', 'irons', 'irez', 'iront'],
    passeCompose: ['suis allé', 'es allé', 'est allé', 'sommes allés', 'êtes allés', 'sont allés'],
  },
  {
    infinitif: 'faire',
    niveau: 3,
    rang: 4,
    aux: 'avoir',
    present: ['fais', 'fais', 'fait', 'faisons', 'faites', 'font'],
    imparfait: ['faisais', 'faisais', 'faisait', 'faisions', 'faisiez', 'faisaient'],
    futur: ['ferai', 'feras', 'fera', 'ferons', 'ferez', 'feront'],
    passeCompose: ['ai fait', 'as fait', 'a fait', 'avons fait', 'avez fait', 'ont fait'],
  },
  {
    infinitif: 'grandir',
    niveau: 4,
    rang: 3,
    aux: 'avoir',
    present: ['grandis', 'grandis', 'grandit', 'grandissons', 'grandissez', 'grandissent'],
    imparfait: ['grandissais', 'grandissais', 'grandissait', 'grandissions', 'grandissiez', 'grandissaient'],
    futur: ['grandirai', 'grandiras', 'grandira', 'grandirons', 'grandirez', 'grandiront'],
    passeCompose: ['ai grandi', 'as grandi', 'a grandi', 'avons grandi', 'avez grandi', 'ont grandi'],
  },
  {
    infinitif: 'prendre',
    niveau: 4,
    rang: 5,
    aux: 'avoir',
    present: ['prends', 'prends', 'prend', 'prenons', 'prenez', 'prennent'],
    imparfait: ['prenais', 'prenais', 'prenait', 'prenions', 'preniez', 'prenaient'],
    futur: ['prendrai', 'prendras', 'prendra', 'prendrons', 'prendrez', 'prendront'],
    passeCompose: ['ai pris', 'as pris', 'a pris', 'avons pris', 'avez pris', 'ont pris'],
  },
  {
    infinitif: 'voir',
    niveau: 5,
    rang: 5,
    aux: 'avoir',
    present: ['vois', 'vois', 'voit', 'voyons', 'voyez', 'voient'],
    imparfait: ['voyais', 'voyais', 'voyait', 'voyions', 'voyiez', 'voyaient'],
    futur: ['verrai', 'verras', 'verra', 'verrons', 'verrez', 'verront'],
    passeCompose: ['ai vu', 'as vu', 'a vu', 'avons vu', 'avez vu', 'ont vu'],
  },
  {
    infinitif: 'venir',
    niveau: 5,
    rang: 5,
    aux: 'etre',
    present: ['viens', 'viens', 'vient', 'venons', 'venez', 'viennent'],
    imparfait: ['venais', 'venais', 'venait', 'venions', 'veniez', 'venaient'],
    futur: ['viendrai', 'viendras', 'viendra', 'viendrons', 'viendrez', 'viendront'],
    passeCompose: ['suis venu', 'es venu', 'est venu', 'sommes venus', 'êtes venus', 'sont venus'],
  },
  {
    infinitif: 'pouvoir',
    niveau: 6,
    rang: 6,
    aux: 'avoir',
    present: ['peux', 'peux', 'peut', 'pouvons', 'pouvez', 'peuvent'],
    imparfait: ['pouvais', 'pouvais', 'pouvait', 'pouvions', 'pouviez', 'pouvaient'],
    futur: ['pourrai', 'pourras', 'pourra', 'pourrons', 'pourrez', 'pourront'],
    passeCompose: ['ai pu', 'as pu', 'a pu', 'avons pu', 'avez pu', 'ont pu'],
  },
  {
    infinitif: 'dire',
    niveau: 6,
    rang: 6,
    aux: 'avoir',
    present: ['dis', 'dis', 'dit', 'disons', 'dites', 'disent'],
    imparfait: ['disais', 'disais', 'disait', 'disions', 'disiez', 'disaient'],
    futur: ['dirai', 'diras', 'dira', 'dirons', 'direz', 'diront'],
    passeCompose: ['ai dit', 'as dit', 'a dit', 'avons dit', 'avez dit', 'ont dit'],
  },
  {
    infinitif: 'écrire',
    niveau: 6,
    rang: 6,
    aux: 'avoir',
    present: ['écris', 'écris', 'écrit', 'écrivons', 'écrivez', 'écrivent'],
    imparfait: ['écrivais', 'écrivais', 'écrivait', 'écrivions', 'écriviez', 'écrivaient'],
    futur: ['écrirai', 'écriras', 'écrira', 'écrirons', 'écrirez', 'écriront'],
    passeCompose: ['ai écrit', 'as écrit', 'a écrit', 'avons écrit', 'avez écrit', 'ont écrit'],
  },
]

/** Le tableau d'un temps donne. */
export function tableau(v: Verbe, t: Temps): readonly string[] {
  if (t === 'present') return v.present
  if (t === 'imparfait') return v.imparfait
  if (t === 'futur') return v.futur
  return v.passeCompose
}

/** Vrai si le mot commence par une voyelle ou un h muet: « j’ai », « j’étais ». */
export function elision(mot: string): boolean {
  const c = mot.charAt(0).toLowerCase()
  return 'aeiouyàâäéèêëîïôöùûü'.includes(c) || c === 'h'
}

/**
 * Le pronom colle a sa forme: « je mange », mais « j’ai ». C'est ce qui permet
 * de proposer des choix complets, sans qu'une apostrophe trahisse la reponse.
 */
export function avecPronom(personne: number, forme: string): string {
  const p = PRONOMS[personne] ?? 'il'
  if (personne === 0 && elision(forme)) return `j’${forme}`
  return `${p} ${forme}`
}

// ----------------------------------------------------------- participes passes

/** Un participe passe et ses quatre accords. */
export interface Participe {
  infinitif: string
  ms: string
  fs: string
  mp: string
  fp: string
  aux: 'avoir' | 'etre'
  /** Fin de phrase, choisie pour que la phrase reste juste. */
  suite: string
}

export const PARTICIPES: readonly Participe[] = [
  { infinitif: 'manger', ms: 'mangé', fs: 'mangée', mp: 'mangés', fp: 'mangées', aux: 'avoir', suite: 'une pomme' },
  { infinitif: 'jouer', ms: 'joué', fs: 'jouée', mp: 'joués', fp: 'jouées', aux: 'avoir', suite: 'au ballon' },
  { infinitif: 'finir', ms: 'fini', fs: 'finie', mp: 'finis', fp: 'finies', aux: 'avoir', suite: 'son devoir' },
  { infinitif: 'voir', ms: 'vu', fs: 'vue', mp: 'vus', fp: 'vues', aux: 'avoir', suite: 'un écureuil' },
  { infinitif: 'faire', ms: 'fait', fs: 'faite', mp: 'faits', fp: 'faites', aux: 'avoir', suite: 'un gâteau' },
  { infinitif: 'aimer', ms: 'aimé', fs: 'aimée', mp: 'aimés', fp: 'aimées', aux: 'avoir', suite: 'ce livre' },
  { infinitif: 'aller', ms: 'allé', fs: 'allée', mp: 'allés', fp: 'allées', aux: 'etre', suite: 'à l’école' },
  { infinitif: 'venir', ms: 'venu', fs: 'venue', mp: 'venus', fp: 'venues', aux: 'etre', suite: 'en autobus' },
  { infinitif: 'partir', ms: 'parti', fs: 'partie', mp: 'partis', fp: 'parties', aux: 'etre', suite: 'très tôt' },
  { infinitif: 'tomber', ms: 'tombé', fs: 'tombée', mp: 'tombés', fp: 'tombées', aux: 'etre', suite: 'dans la neige' },
  { infinitif: 'arriver', ms: 'arrivé', fs: 'arrivée', mp: 'arrivés', fp: 'arrivées', aux: 'etre', suite: 'en retard' },
  { infinitif: 'rester', ms: 'resté', fs: 'restée', mp: 'restés', fp: 'restées', aux: 'etre', suite: 'à la maison' },
  { infinitif: 'sortir', ms: 'sorti', fs: 'sortie', mp: 'sortis', fp: 'sorties', aux: 'etre', suite: 'sans manteau' },
]

export function accorderParticipe(p: Participe, genre: 'm' | 'f', pluriel: boolean): string {
  if (genre === 'm') return pluriel ? p.mp : p.ms
  return pluriel ? p.fp : p.fs
}

export function formesParticipe(p: Participe): readonly string[] {
  return [p.ms, p.fs, p.mp, p.fp]
}
